import type { CustomChartGrammar } from '@erp/viz/custom';
import {
  REPORT_COMPONENT_SCHEMAS,
  normalizeReportComponent,
  reportCanvasComponents,
  type CanvasComponentConfig,
  type WorkbookDefinition,
  type WorkbookReportOutput,
} from '@erp/shared-types';
import type { ReportDatasetRow } from '@/components/report-canvas/ReportDataContext';
import { validateCustomSpec } from '@/components/report-canvas/vega/validateCustomSpec';
import { defaultReportDatasetKey } from '@erp/workbooks';
import { WorkbookAgentEditError } from './workbookAgentEdits';
import {
  workbookReportSheets,
  type WorkbookReportSheet,
} from './workbookReportDataset';

/** A Custom chart's rows to prove its spec against, or undefined when none can be read. */
export type ReportChartRows = (
  sheetId: string,
) => Promise<readonly ReportDatasetRow[] | undefined>;

/**
 * What the gate needs of each Report component type: the config fields that
 * name a column of its Worksheet (the Report registry's `columnRefFields`).
 * A type missing here is not a Report component.
 */
export type ReportComponentBindings = Readonly<
  Record<string, { readonly columnRefFields?: readonly string[] }>
>;

type ReportComponent = {
  outputId: string;
  component: CanvasComponentConfig;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function reports(definition: WorkbookDefinition): WorkbookReportOutput[] {
  return definition.outputs.filter(
    (output): output is WorkbookReportOutput => output.kind === 'report',
  );
}

function reportComponents(definition: WorkbookDefinition): ReportComponent[] {
  return reports(definition).flatMap((output) =>
    reportCanvasComponents(output.layout).map((component) => ({
      outputId: output.id,
      component,
    })),
  );
}

const componentKey = (entry: ReportComponent) =>
  `${entry.outputId}/${entry.component.id}`;

function withComponents(
  definition: WorkbookDefinition,
  replaced: ReadonlyMap<string, CanvasComponentConfig>,
): WorkbookDefinition {
  if (!replaced.size) return definition;
  const replace = (outputId: string, component: CanvasComponentConfig) =>
    replaced.get(componentKey({ outputId, component })) ?? component;
  return {
    ...definition,
    outputs: definition.outputs.map((output) => {
      if (output.kind !== 'report') return output;
      const layout = output.layout;
      return {
        ...output,
        layout:
          'items' in layout
            ? {
                items: layout.items.map((item) => ({
                  ...item,
                  component: replace(output.id, item.component),
                })),
              }
            : {
                data: layout.data.map((row) =>
                  row.map((component) => replace(output.id, component)),
                ),
              },
      };
    }),
  };
}

/** How text is compared when it names a Worksheet or a column loosely. */
const loose = (text: string) => text.trim().toLowerCase();

/** The one entry `name` names exactly, else the one it names ignoring case and spaces around it. */
function findByName<T>(
  entries: readonly T[],
  nameOf: (entry: T) => string,
  name: string,
): T | undefined {
  const exact = entries.find((entry) => nameOf(entry) === name);
  if (exact) return exact;
  const matches = entries.filter(
    (entry) => loose(nameOf(entry)) === loose(name),
  );
  return matches.length === 1 ? matches[0] : undefined;
}

const quoted = (names: readonly string[]) =>
  names.map((name) => `"${name}"`).join(', ');

/** A component's bindings resolved: repaired to keys where they name one loosely, or why they can't be. */
type Resolution = {
  component: CanvasComponentConfig;
  sheet: WorkbookReportSheet | undefined;
  errors: string[];
  warnings: string[];
};

/**
 * The Worksheet a component charts: its datasetKey (a Worksheet id, or a
 * Worksheet's name, which is repaired to its id), else the first Worksheet.
 */
function resolveSheet(
  component: CanvasComponentConfig,
  sheets: readonly WorkbookReportSheet[],
  label: string,
): Pick<Resolution, 'component' | 'sheet' | 'errors'> {
  const { datasetKey } = component;
  if (datasetKey === undefined)
    return { component, sheet: sheets[0], errors: [] };
  const byId = sheets.find((sheet) => sheet.key === datasetKey);
  if (byId) return { component, sheet: byId, errors: [] };
  const byName = findByName(sheets, (sheet) => sheet.label, datasetKey);
  if (byName)
    return {
      component: { ...component, datasetKey: byName.key },
      sheet: byName,
      errors: [],
    };
  return {
    component,
    sheet: undefined,
    errors: [
      `${label}: datasetKey "${datasetKey}" is not a worksheet; use a worksheet id (${sheets
        .map((sheet) => `${sheet.key} for "${sheet.label}"`)
        .join(', ')}), or leave it out for the first worksheet.`,
    ],
  };
}

/**
 * The column fields of a component resolved against its Worksheet's columns:
 * a column named in another case or with spaces around it is repaired to its
 * name; an unset field or one naming no column is refused, since the chart
 * would show "Not set up yet" or draw nothing. A pivot's columns can come
 * from its data, so a column it may yet have is a warning, not a refusal.
 */
function resolveColumns(
  component: CanvasComponentConfig,
  fields: readonly string[],
  sheet: WorkbookReportSheet,
  label: string,
): Pick<Resolution, 'component' | 'errors' | 'warnings'> {
  const columns = sheet.columns.map((column) => column.name);
  const known = `columns of worksheet "${sheet.label}": ${quoted(columns)}`;
  const next: Record<string, unknown> = { ...component };
  const unset: string[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];
  for (const field of fields) {
    const value = component[field];
    const column = boundColumn(value, columns);
    if (column === undefined) unset.push(field);
    else if (column !== null) next[field] = column;
    else {
      const reason = `${label}: ${field} ${JSON.stringify(value)} is not a column of worksheet "${sheet.label}" (its ${known})`;
      // A pivot's columns can come from its data: it may yet have this one.
      const mayHave = sheet.sheet.kind === 'pivot' && typeof value === 'string';
      (mayHave ? warnings : errors).push(reason);
    }
  }
  if (unset.length)
    errors.push(
      `${label}: ${unset.join(', ')} ${unset.length === 1 ? 'is' : 'are'} not set; set ${unset.length === 1 ? 'it' : 'them'} to ${known}. Read report_component_catalog for the fields each component type takes.`,
    );
  return { component: next as CanvasComponentConfig, errors, warnings };
}

/** The column a field's value names among `columns`: undefined while unset, null when it names no column. */
function boundColumn(
  value: unknown,
  columns: readonly string[],
): string | null | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') return null;
  return findByName(columns, (name) => name, value) ?? null;
}

/** A changed component's Worksheet and columns resolved for the Report to chart. */
function resolveBindings(
  component: CanvasComponentConfig,
  sheets: readonly WorkbookReportSheet[],
  bindings: ReportComponentBindings,
): Resolution {
  const label = `${component.type} ${component.id}`;
  const binding = bindings[component.type];
  if (!binding)
    return {
      component,
      sheet: undefined,
      errors: [
        `${label}: "${component.type}" is not a report component type; use one of ${quoted(Object.keys(bindings))}.`,
      ],
      warnings: [],
    };
  const resolved = resolveSheet(component, sheets, label);
  const fields = binding.columnRefFields ?? [];
  if (!resolved.sheet || !fields.length) return { ...resolved, warnings: [] };
  const columns = resolveColumns(
    resolved.component,
    fields,
    resolved.sheet,
    label,
  );
  return {
    component: columns.component,
    sheet: resolved.sheet,
    errors: [...resolved.errors, ...columns.errors],
    warnings: columns.warnings,
  };
}

/** Why a component's fields don't fit its type: unknown fields, or values of the wrong kind. */
export function componentShapeErrors(
  component: Pick<CanvasComponentConfig, 'id' | 'type'>,
): string[] {
  const schema = REPORT_COMPONENT_SCHEMAS[component.type];
  if (!schema) return [];
  const result = schema.strict().safeParse(component);
  if (result.success) return [];
  return result.error.issues.map(
    (issue) =>
      `${component.type} ${component.id}: ${issue.path.length ? `${issue.path.join('.')}: ` : ''}${issue.message}. Set each field directly on the component, as report_component_catalog shows it.`,
  );
}

/** A Custom chart's spec through its gate: the spec to persist, or why it is refused. */
async function gateCustomChart(
  component: CanvasComponentConfig,
  sheetId: string | undefined,
  rowsFor: ReportChartRows,
): Promise<{
  spec?: Record<string, unknown>;
  errors: string[];
  warnings: string[];
}> {
  if (!isRecord(component.spec)) return { errors: [], warnings: [] };
  const grammar: CustomChartGrammar =
    component.grammar === 'vega' ? 'vega' : 'vega-lite';
  const rows = sheetId ? await rowsFor(sheetId) : undefined;
  const check = await validateCustomSpec(grammar, component.spec, rows);
  const name = `Custom chart ${component.id}`;
  if (check.error !== null)
    return { errors: [`${name}: ${check.error}`], warnings: [] };
  return {
    spec: check.spec,
    errors: [],
    warnings: [
      ...(rows
        ? []
        : [
            `${name} was checked without data: its worksheet has no result small enough to chart in the browser yet.`,
          ]),
      ...check.warnings.map((warning) => `${name}: ${warning}`),
    ],
  };
}

/**
 * Gates every Report component the agent added or changed before anything
 * applies; unchanged components are not rechecked. Each must be a Report
 * component type charting a Worksheet (its datasetKey a Worksheet id; a
 * Worksheet's name is repaired to its id) with every column field set to a
 * column of that Worksheet (named in another case, repaired to its name;
 * fields nested under `config`, lifted onto the component): an
 * unbound chart shows "Not set up yet" in place of a chart, so it is refused
 * with the fields and columns to use. Every Custom chart spec passes the one
 * gate a spec enters a Report by ({@link validateCustomSpec}), proved against
 * its Worksheet's rows when they can be read. Returns the Definition as it
 * should be persisted and the doubts; throws a {@link WorkbookAgentEditError}
 * listing every refusal, so nothing applies.
 */
export async function gateReportCharts(
  previous: WorkbookDefinition,
  next: WorkbookDefinition,
  rowsFor: ReportChartRows,
  bindings: ReportComponentBindings,
): Promise<{ definition: WorkbookDefinition; warnings: string[] }> {
  const before = new Map(
    reportComponents(previous).map((entry) => [
      componentKey(entry),
      JSON.stringify(entry.component),
    ]),
  );
  const changed = reportComponents(next).filter(
    (entry) =>
      before.get(componentKey(entry)) !== JSON.stringify(entry.component),
  );
  const sheets = workbookReportSheets(next);
  const defaultKey = defaultReportDatasetKey(next);
  const errors: string[] = [];
  const warnings: string[] = [];
  const replaced = new Map<string, CanvasComponentConfig>();
  for (const entry of changed) {
    const resolved = resolveBindings(
      normalizeReportComponent(entry.component),
      sheets,
      bindings,
    );
    errors.push(...resolved.errors);
    warnings.push(...resolved.warnings);
    let component = resolved.component;
    if (component.type === 'vega-chart') {
      const custom = await gateCustomChart(
        component,
        resolved.sheet?.key ?? defaultKey,
        rowsFor,
      );
      errors.push(...custom.errors);
      warnings.push(...custom.warnings);
      if (custom.spec) component = { ...component, spec: custom.spec };
    }
    errors.push(...componentShapeErrors(component));
    if (component !== entry.component)
      replaced.set(componentKey(entry), component);
  }
  if (errors.length)
    throw new WorkbookAgentEditError(
      `Refused report components:\n${errors.join('\n')}`,
    );
  return { definition: withComponents(next, replaced), warnings };
}
